import React, { useRef, useLayoutEffect } from 'react';
import { TabWrapper, Indicator, TabsRoot } from './Tabs.styles';
import { ToggleItem, ToggleItemProps } from './components/ToggleItem/ToggleItem';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useSpring } from 'react-spring';
import { isNumber } from 'lodash-es';
import { TextV2Props } from '@withjoy/joykit';

export type TabItemProps = Pick<ToggleItemProps, 'label' | 'value' | 'isActive' | 'onClick' | 'isDisabled'>;

interface Props {
  items: TabItemProps[];
  isMobile?: boolean;
  initialIndicatorWidth?: number | string;
  width?: string;
  tabTextProps?: TextV2Props;
}

const Tabs: React.SFC<Props> = ({ items, isMobile, initialIndicatorWidth, width: TabsRootWidth, tabTextProps }) => {
  const isMounted = useIsMounted();
  const tabsContainer = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);

  const [{ left, width: tabWidth }, set] = useSpring(() => ({ width: initialIndicatorWidth ? initialIndicatorWidth : 0, left: 0 }));

  useLayoutEffect(() => {
    if (isMounted) {
      if (indicatorRef.current) {
        if (tabsContainer.current) {
          const children = Array.from(tabsContainer.current.children);
          const activeTab = (children as HTMLElement[]).find(child => child.classList.contains('active'));
          if (activeTab) {
            const { width } = activeTab.getBoundingClientRect();
            set({ left: activeTab.offsetLeft, width: isNumber(initialIndicatorWidth) && width > initialIndicatorWidth ? initialIndicatorWidth : width });
          }
        }
      }
    }
  });

  return (
    <TabsRoot width={TabsRootWidth}>
      <TabWrapper ref={tabsContainer}>
        {items.map(item => (
          <ToggleItem
            key={item.label}
            isMobile={isMobile}
            isDisabled={item.isDisabled}
            textProps={tabTextProps}
            {...item}
            minWidth={`${initialIndicatorWidth}px`}
            onClick={e => {
              if (!item.isActive) {
                item.onClick?.(e);
              }
            }}
          />
        ))}
      </TabWrapper>
      <Indicator
        ref={indicatorRef}
        style={{ transform: left.interpolate(value => `translate3d(${value}px, -50%, 0px)`), willChange: 'transform', width: tabWidth }}
      />
    </TabsRoot>
  );
};

export { Tabs };
